/**
 * Which of the editor's advanced tools are switched on.
 *
 * The editor ships in its simple form: a toolbox organised by what is on the plan — rooms,
 * floor, border, desks, labels, basic shapes — and a panel that lists what can be done to
 * whatever was clicked. The power tools built alongside it are all still here, and come back
 * one phase at a time by flipping the switch below rather than by being rewritten.
 *
 * Kept as plain constants rather than a runtime setting on purpose: each of these is a product
 * decision to roll a tool out, not a preference for an administrator to discover by accident.
 */
export interface EditorFeatureFlags {
  /** The left column: layers, import, history, find, automation and validation. */
  readonly sidePanels: boolean;
  /** Align, distribute, grid copies and move-to-layer, above the properties panel. */
  readonly bulkActions: boolean;
  /** The Ctrl/⌘ + K command list, its status-bar button and the Ctrl/⌘ + F find panel. */
  readonly commandPalette: boolean;
  /**
   * The original drawing rail — vertex, polygon, rectangle, ellipse, furniture — in place of
   * the simple toolbox, along with the "draw into" layer picker in the status bar.
   */
  readonly legacyToolRail: boolean;
  /**
   * The Snap switches at the bottom right of the plan — to shapes and to a grid. Off, nothing
   * snaps: the settings stay at their defaults, which are both off.
   */
  readonly snapping: boolean;
  /**
   * Importing an AutoCAD drawing (DXF) — in the dashboard's floorplan upload, on the page for
   * an office with no plan yet, and in the editor's import panel. Off, GeoJSON is the only way
   * in. The server endpoints stay; only the way to reach them is hidden.
   */
  readonly drawingImport: boolean;
}

export const EDITOR_FEATURES: EditorFeatureFlags = {
  sidePanels: false,
  bulkActions: false,
  commandPalette: false,
  legacyToolRail: false,
  snapping: false,
  drawingImport: false,
};

export default EDITOR_FEATURES;
